import { useEffect, useState } from "react";
import { CHURCH } from "@/lib/church";
import { X, PhoneCall, PaperPlaneTilt } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const OPTIONS = [
    "I need prayer",
    "I want to talk",
    "I'm visiting for the first time",
    "I need support or guidance",
];

// Olive branch mini-icon
function OliveIcon({ size = 20, className = "" }) {
    return (
        <svg viewBox="0 0 24 24" width={size} height={size} className={className} fill="none">
            <path d="M4 18 C 10 14, 15 10, 20 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            <ellipse cx="9" cy="16" rx="2.3" ry="1.4" transform="rotate(-30 9 16)" fill="currentColor" opacity="0.55" />
            <ellipse cx="13" cy="12.5" rx="2.3" ry="1.4" transform="rotate(-30 13 12.5)" fill="currentColor" opacity="0.75" />
            <ellipse cx="17.5" cy="8.5" rx="2.6" ry="1.6" transform="rotate(-30 17.5 8.5)" fill="currentColor" />
        </svg>
    );
}

export default function OlivePocket() {
    const [open, setOpen] = useState(false);
    const [pulse, setPulse] = useState(true);
    const [selected, setSelected] = useState([]);
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [note, setNote] = useState("");
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);

    // Restart pulse every ~10s (only while closed)
    useEffect(() => {
        if (open) return;
        setPulse(true);
        const t = setInterval(() => {
            setPulse(false);
            setTimeout(() => setPulse(true), 60);
        }, 10000);
        return () => clearInterval(t);
    }, [open]);

    const toggle = (opt) => {
        setSelected((prev) => (prev.includes(opt) ? prev.filter((x) => x !== opt) : [...prev, opt]));
    };

    const send = async (e) => {
        e.preventDefault();
        if (!name.trim() || !email.trim()) {
            toast.error("Please share your name and email.");
            return;
        }
        setSending(true);
        try {
            await api.post("/contact", {
                name,
                email,
                phone: "",
                message: `[The Olive – House²House Pocket]\n\nWays we can walk with you:\n${selected.length ? selected.map((s) => "· " + s).join("\n") : "· (none selected)"}\n\nNote from ${name}:\n${note || "(no additional note)"}`,
                type: "house2house",
            });
            toast.success("Message sent — someone from The Olive will be in touch.");
            setSelected([]); setName(""); setEmail(""); setNote("");
            setSent(true);
        } catch (_e) {
            toast.error("Something went wrong. Please try again or call us directly.");
        } finally {
            setSending(false);
        }
    };

    const closePanel = () => {
        setOpen(false);
        setSent(false);
    };

    return (
        <>
            {/* CLOSED PILL */}
            {!open && (
                <button
                    type="button"
                    onClick={() => setOpen(true)}
                    data-testid="olive-pocket-toggle"
                    className={`fixed z-[60] top-1/2 -translate-y-1/2 right-4 sm:right-5 inline-flex items-center gap-3 pl-3 pr-5 py-3 rounded-full text-white shadow-[0_18px_55px_-10px_rgba(236,72,153,0.7)] transition-all hover:scale-105 ring-2 ring-[#FFC72C] hover:ring-[#FFF7B2] ${pulse ? 'olive-pulse-bright' : ''}`}
                    style={{ backgroundImage: "linear-gradient(120deg, #EC4899 0%, #A855F7 55%, #4C1D95 100%)" }}
                >
                    <span className="w-9 h-9 rounded-full bg-[#B7D26A] flex items-center justify-center text-[#2E1A47] shadow-inner">
                        <OliveIcon size={20} />
                    </span>
                    <span className="flex flex-col items-start leading-none text-left">
                        <span className="text-[13px] font-black tracking-tight">We Are the Olive!</span>
                        <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#FFF7B2] mt-1 flex items-center gap-1">
                            Tap to Chat <span aria-hidden="true">→</span>
                        </span>
                    </span>
                </button>
            )}

            {/* OPEN CARD */}
            {open && (
                <div
                    role="dialog"
                    aria-modal="false"
                    data-testid="olive-pocket-panel"
                    className="fixed z-[60] top-1/2 right-4 sm:right-5 w-[calc(100vw-2rem)] sm:w-[340px] max-h-[calc(100vh-2rem)] bg-white shadow-[0_25px_60px_-15px_rgba(236,72,153,0.55)] rounded-3xl overflow-hidden animate-olive-in ring-2 ring-[#FFC72C]"
                >
                    {/* Header */}
                    <div className="relative bg-gradient-to-br from-[#EC4899] via-[#A855F7] to-[#4C1D95] text-white px-4 py-3 pr-14">
                        <button
                            type="button"
                            onClick={closePanel}
                            data-testid="olive-pocket-close"
                            aria-label="Close chat"
                            title="Close"
                            className="absolute top-2 right-2 w-10 h-10 rounded-full bg-white text-[#4C1D95] hover:bg-[#FFC72C] hover:text-[#2E1A47] flex items-center justify-center transition-colors shadow-lg ring-2 ring-white/60"
                        >
                            <X size={18} weight="bold" />
                        </button>
                        <div className="flex items-center gap-2 mb-1">
                            <span className="w-6 h-6 rounded-full bg-[#B7D26A] flex items-center justify-center text-[#2E1A47] shadow-inner">
                                <OliveIcon size={14} />
                            </span>
                            <p className="text-[9px] uppercase tracking-[0.25em] text-[#FFF7B2] font-black">Let&rsquo;s Talk!</p>
                        </div>
                        <h3 className="font-display text-lg font-black tracking-tight leading-tight">Welcome to <span className="italic text-[#FFC72C]">The Olive</span>.</h3>
                        <p className="text-[11px] text-white font-semibold mt-0.5 leading-snug">Greater Mount Olive&rsquo;s House&sup2;House online ministry.</p>
                    </div>

                    {/* Body */}
                    {sent ? (
                        <div className="p-6 text-center space-y-4" data-testid="olive-pocket-sent">
                            <div className="mx-auto w-14 h-14 rounded-full bg-gradient-to-br from-[#EC4899] to-[#4C1D95] flex items-center justify-center text-white">
                                <svg viewBox="0 0 24 24" width="26" height="26" fill="none"><path d="M5 12.5 L10 17.5 L19 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                            </div>
                            <div>
                                <p className="font-display text-lg font-black text-[#2E1A47]">Message on its way.</p>
                                <p className="text-xs text-slate-600 mt-1">Someone from <em className="not-italic font-bold text-[#4C1D95]">The Olive</em> will be in touch soon.</p>
                            </div>
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={() => setSent(false)}
                                    data-testid="olive-send-another"
                                    className="flex-1 px-3 py-2.5 rounded-full bg-slate-100 hover:bg-slate-200 text-[10px] font-black uppercase tracking-[0.2em] text-[#2E1A47] transition-colors"
                                >
                                    Send Another
                                </button>
                                <button
                                    type="button"
                                    onClick={closePanel}
                                    data-testid="olive-close-btn"
                                    className="flex-1 px-3 py-2.5 rounded-full bg-[#FFC72C] hover:bg-[#E6B327] text-[10px] font-black uppercase tracking-[0.2em] text-[#2E1A47] transition-colors"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    ) : (
                    <form onSubmit={send} className="p-4 space-y-3">
                        <div>
                            <p className="text-[10px] uppercase tracking-widest font-black text-[#4C1D95] mb-2">How can we walk with you today?</p>
                            <div className="space-y-1.5">
                                {OPTIONS.map((opt) => {
                                    const active = selected.includes(opt);
                                    return (
                                        <button
                                            key={opt}
                                            type="button"
                                            onClick={() => toggle(opt)}
                                            data-testid={`olive-option-${opt.replace(/[^a-z]/gi, "-").toLowerCase()}`}
                                            className={`w-full text-left flex items-center gap-2.5 px-3 py-1.5 rounded-full text-[13px] transition-all ${active ? 'bg-[#4C1D95] text-white shadow-md' : 'bg-slate-100 text-[#2E1A47] hover:bg-[#FFC72C]/25'}`}
                                        >
                                            <span className={`w-3.5 h-3.5 rounded-sm flex items-center justify-center shrink-0 ${active ? 'bg-[#FFC72C]' : 'bg-white border border-slate-300'}`}>
                                                {active && <svg viewBox="0 0 12 12" width="9" height="9" fill="none"><path d="M2 6.5 L5 9 L10 3" stroke="#2E1A47" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>}
                                            </span>
                                            {opt}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-2">
                            <input
                                data-testid="olive-name-input"
                                required
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Your name"
                                className="px-3 py-1.5 rounded-full bg-slate-100 text-[13px] text-[#2E1A47] placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#4C1D95]"
                            />
                            <input
                                data-testid="olive-email-input"
                                required
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="Email"
                                className="px-3 py-1.5 rounded-full bg-slate-100 text-[13px] text-[#2E1A47] placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#4C1D95]"
                            />
                        </div>
                        <textarea
                            data-testid="olive-note-input"
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder="Tell us a little (optional)"
                            rows={2}
                            className="w-full px-3 py-1.5 rounded-2xl bg-slate-100 text-[13px] text-[#2E1A47] placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#4C1D95] resize-none"
                        />

                        <button
                            type="submit"
                            disabled={sending}
                            data-testid="olive-send-btn"
                            className="w-full inline-flex items-center justify-center gap-2 bg-[#FFC72C] hover:bg-[#E6B327] disabled:opacity-70 text-[#2E1A47] px-4 py-2.5 rounded-full text-[10px] font-black uppercase tracking-[0.25em] transition-colors"
                        >
                            {sending ? "Sending…" : (<><PaperPlaneTilt size={13} weight="fill" /> Send to The Olive</>)}
                        </button>

                        <div className="pt-2 border-t border-slate-100">
                            <p className="text-[9px] uppercase tracking-widest text-slate-500 font-bold mb-1.5 text-center">Prefer to speak with someone now?</p>
                            <a
                                href={CHURCH.phoneHref}
                                data-testid="olive-phone-link"
                                className="w-full inline-flex items-center justify-center gap-2 border-2 border-[#4C1D95] hover:bg-[#4C1D95] hover:text-white text-[#4C1D95] px-4 py-2 rounded-full text-[13px] font-black transition-colors"
                            >
                                <PhoneCall size={14} weight="fill" /> {CHURCH.phone}
                            </a>
                            <button
                                type="button"
                                onClick={closePanel}
                                data-testid="olive-notnow-btn"
                                className="w-full text-center text-[11px] text-slate-500 hover:text-[#4C1D95] font-bold uppercase tracking-widest mt-2 py-1 transition-colors"
                            >
                                Not now &middot; Close
                            </button>
                        </div>
                    </form>
                    )}
                </div>
            )}
        </>
    );
}
